import { RepriseOptions } from 'features/arretTravail/models/StopInformation/reprise';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

export const useSalaryBaseS6202Schema = (store) => {
    const { t } = useTranslation();
    const requiredMessage = t('yup.validators.required');
    const startDateBeforeEndDate = t('La fin de la période de référence doit être <= au début');
    const endDateBeforeReturnToWorkDate = t('La date de fin doit être <= à la date de reprise');
    const amountComparisonMessage = t('Les cotisation à déduire doivent être < ');
    const overlapMessage = t('Les périodes de référence se chevauchent');

    const isTherapeutique = store.workStoppage.infos.returnToWorkCod === RepriseOptions.PartielTherapeutique;

    return Yup.object().shape({
        salaryBaseS6202: Yup.object({
            salaries: Yup.array()
                .of(
                    Yup.object({
                        id: Yup.string().required(),
                        payrollDueDate: Yup.date().nullable().required(requiredMessage),
                        startDate: Yup.date().nullable().required(requiredMessage),
                        endDate: Yup.date()
                            .nullable()
                            .required(requiredMessage)
                            .test('endDate-before-lastDayWorked', endDateBeforeReturnToWorkDate, function (value) {
                                let lastDayWorked = store.workStoppage.infos.returnToWorkDate;
                                return !value || !lastDayWorked || value <= lastDayWorked;
                            })
                            .test('endDate-after-startDate', startDateBeforeEndDate, function (value) {
                                const { startDate } = this.parent;
                                return !value || !startDate || value >= startDate;
                            }),
                        amount: Yup.string().nullable().required(requiredMessage),
                        cotisation: Yup.string()
                            .nullable()
                            .required(requiredMessage)
                            .test('cotisation-less-than-amount', function (value) {
                                const { amount } = this.parent;
                                const errorMessage = amountComparisonMessage + (amount ? ` ${amount}` : '');
                                return !value || !amount || parseFloat(value) < parseFloat(amount) || this.createError({ message: errorMessage });
                            }),
                    }),
                )
                .required()
                .test('no-overlapping-dates', overlapMessage, function (salaries) {
                    if (!salaries || salaries.length < 2) return true;

                    for (let i = 0; i < salaries.length; i++) {
                        const { startDate: startA, endDate: endA } = salaries[i];
                        if (!startA || !endA) continue;

                        for (let j = i + 1; j < salaries.length; j++) {
                            const { startDate: startB, endDate: endB } = salaries[j];
                            if (!startB || !endB) continue;
                            if (startA <= endB && startB <= endA) {
                                return this.createError({
                                    message: overlapMessage,
                                    path: `salaryBaseS6202.salaries[${j}].startDate`,
                                });
                                // TODO :: improve message =>"La période de référence chevauche la période du
                            }
                        }
                    }
                    return true;
                }),
            salariesMT: Yup.array()
                .of(
                    Yup.object({
                        id: Yup.string().required(),
                        payrollDueDate: isTherapeutique ? Yup.date().nullable().required(requiredMessage) : Yup.date().nullable(),
                        startDate: isTherapeutique
                            ? Yup.date()
                                  .nullable()
                                  .required(requiredMessage)
                                  .test('startDate-before-lastDayWorked', endDateBeforeReturnToWorkDate, function (value) {
                                      let lastDayWorked = store.workStoppage.infos.returnToWorkDate;
                                      return !value || !lastDayWorked || value <= lastDayWorked;
                                  })
                                  .test('startDate-before-today', endDateBeforeReturnToWorkDate, function (value) {
                                      let lastDayWorked = store.workStoppage.infos.returnToWorkDate;
                                      return !value || !lastDayWorked || value <= lastDayWorked;
                                  })
                                  .test('startDate-before-today', t("Le début de la période de référence doit être <= aujourd'hui"), function (value) {
                                      const today = new Date();
                                      return !value || value <= today;
                                  })
                            : Yup.date().nullable(),
                        endDate: isTherapeutique
                            ? Yup.date()
                                  .nullable()
                                  .required(requiredMessage)
                                  .test('endDate-before-lastDayWorked', endDateBeforeReturnToWorkDate, function (value) {
                                      let lastDayWorked = store.workStoppage.infos.returnToWorkDate;
                                      console.log(lastDayWorked);
                                      return !value || !lastDayWorked || value <= lastDayWorked;
                                  })
                                  .test('endDate-after-startDate', startDateBeforeEndDate, function (value) {
                                      const { startDate } = this.parent;
                                      return !value || !startDate || value >= startDate;
                                  })
                            : Yup.date().nullable(),
                        amount: isTherapeutique ? Yup.string().nullable().required(requiredMessage) : Yup.string().nullable(),
                        cotisation: isTherapeutique
                            ? Yup.string()
                                  .nullable()
                                  .required(requiredMessage)
                                  .test('cotisation-less-than-amount', function (value) {
                                      const { amount } = this.parent;
                                      const errorMessage = amountComparisonMessage + (amount ? ` ${amount}` : '');
                                      return !value || !amount || parseFloat(value) < parseFloat(amount) || this.createError({ message: errorMessage });
                                  })
                            : Yup.string().nullable(),
                    }),
                )
                .required()
                .test('no-overlapping-dates', overlapMessage, function (salaries) {
                    if (!isTherapeutique) return true;
                    const options: any = this.options;
                    const salariesContext = options.from[0].value.salaries;

                    if (salariesContext?.length >= 1) {
                        const index = salariesContext.length - 1;
                        const lastSalariesEndDate = new Date(salariesContext[index]?.endDate);

                        if (new Date(salaries[0]?.startDate) <= lastSalariesEndDate) {
                            return this.createError({
                                message: overlapMessage,
                                path: `salaryBaseS6202.salariesMT[0].startDate`,
                            });
                        }
                    }

                    if (!salaries || salaries.length < 2) return true;

                    for (let i = 0; i < salaries.length; i++) {
                        const { startDate: startA, endDate: endA } = salaries[i];
                        if (!startA || !endA) continue;

                        for (let j = i + 1; j < salaries.length; j++) {
                            const { startDate: startB, endDate: endB } = salaries[j];
                            if (!startB || !endB) continue;
                            if (startA <= endB && startB <= endA) {
                                return this.createError({
                                    message: overlapMessage,
                                    path: `salaryBaseS6202.salariesMT[${j}].startDate`,
                                });
                                // TODO :: improve message =>"La période de référence chevauche la période du
                            }
                        }
                    }
                    return true;
                }),
        }).required(),
    });
};
